import { Box, Stack, Tooltip, Typography } from '@mui/joy';
import { get } from 'lodash';
import { ReactNode, SyntheticEvent, useCallback } from 'react';
import { Control, useFieldArray } from 'react-hook-form';

import { useFormContext } from 'core/states';
import { IAutocompleteOption } from 'core/types';
import { Level } from 'theme/enums';

import { ChipRow } from './ChipRow';

interface IPickerProps<T extends IAutocompleteOption = IAutocompleteOption> {
  name: string;
  onChange: (event: SyntheticEvent, option: T) => void;
  placeholder?: string;
  disabled?: boolean;
  // Drives the field array directly so callers can clear/replace items without a
  // raw setValue on the array name (the documented RHF footgun that can desync chips).
  replace: (items: T[]) => void;
}

// An extra chip row pinned at the top of the chip list, rendered with the same markup
// as a real field chip but without a secondary line. Used for sentinel-style selections
// (e.g. "All Customers") that are not backed by an entry in the field array.
interface IPinnedChip {
  label: string;
  onDelete: () => void;
}

interface IProps<T extends IAutocompleteOption = IAutocompleteOption> {
  name: string;
  label: string;
  renderPicker: (props: IPickerProps<T>) => ReactNode;
  placeholder?: string;
  renderItemSecondary?: (item: T) => ReactNode;
  pinnedChip?: IPinnedChip;
  disabled?: boolean;
  disabledTooltip?: string;
}

export const MultiSelectChipList = <T extends IAutocompleteOption = IAutocompleteOption>({
  name,
  label,
  renderPicker,
  placeholder,
  renderItemSecondary,
  pinnedChip,
  disabled = false,
  disabledTooltip,
}: IProps<T>) => {
  const { control, errors, getValues, setValue } = useFormContext();

  const { fields, append, remove, replace } = useFieldArray<{
    [key: string]: IAutocompleteOption[];
  }>({
    control: control as unknown as Control<{
      [key: string]: IAutocompleteOption[];
    }>,
    name,
  });

  const pickerName = `__${name}Picker`;
  const hasError = !!get(errors, name);

  const onPickerChange = useCallback(
    (_event: SyntheticEvent, option: T) => {
      if (disabled || !option?.id) {
        return;
      }

      const currentItems = (getValues(name) as T[] | undefined) ?? [];
      const isAlreadySelected = currentItems.some((item) => item.id === option.id);

      if (!isAlreadySelected) {
        append(option as IAutocompleteOption);
      }

      setValue(pickerName, null, { shouldValidate: false });
    },
    [append, disabled, getValues, name, pickerName, setValue]
  );

  const content = (
    <Box
      // The field array registers no ref with RHF and the picker input has its own name, so a
      // focus request for the field name (focusFirstInvalidField, useFocusInputFunctionality)
      // is routed here.
      data-focus-name={name}
      sx={{
        border: '1px solid',
        borderColor: hasError ? 'danger.outlinedBorder' : 'neutral.outlinedBorder',
        borderRadius: 'sm',
        padding: 2,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Stack sx={{ gap: 1.5 }}>
        <Typography level={Level.TITLE_MD}>{label}</Typography>

        {renderPicker({
          name: pickerName,
          onChange: onPickerChange,
          placeholder,
          disabled,
          replace: replace as unknown as (items: T[]) => void,
        })}

        {(pinnedChip || fields.length > 0) && (
          <Stack sx={{ gap: 1 }}>
            {pinnedChip && (
              <ChipRow
                label={pinnedChip.label}
                disabled={disabled}
                onDelete={pinnedChip.onDelete}
              />
            )}

            {fields.map((field, index) => {
              const item = field as unknown as T;

              return (
                <ChipRow
                  key={field.id}
                  label={item.name}
                  secondary={renderItemSecondary?.(item)}
                  disabled={disabled}
                  onDelete={() => remove(index)}
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    </Box>
  );

  if (disabled && disabledTooltip) {
    return <Tooltip title={disabledTooltip}>{content}</Tooltip>;
  }

  return content;
};
